import React, { useState } from 'react';
import {
  Building2,
  UserPlus,
  ChevronDown,
  ChevronRight,
  X,
  Mail,
  Phone,
  MapPin,
  Hash,
  Users,
  Search,
} from 'lucide-react';
import CreatableSelect from 'react-select/creatable';
import { ClientQuickInput } from '../../clients/ClientQuickInput';
import { DealReferentiSelector } from './DealReferentiSelector';
import { DealWhatsAppButton } from '../DealWhatsAppButton';
import type { Client, ClientContact, DealFormData } from '../../../lib/types';

interface ClientOption {
  value: string;
  label: string;
  type?: string;
  data?: Client;
}

interface QuickClientData {
  business_name: string;
  email?: string;
  phone?: string;
  whatsapp_number?: string;
  entity_type?: 'company' | 'individual';
}

interface DealClientSelectorCardProps {
  variant: 'desktop' | 'mobile';
  isDark: boolean;
  language: string;
  /** Tutte le opzioni cliente per il lookup `value`. */
  allClientOptions: ClientOption[];
  /** Opzioni filtered+ordered per il search desktop (server-side). */
  filteredAndSortedClientOptions: ClientOption[];
  isSearching: boolean;
  clientsLoading: boolean;

  formData: DealFormData;
  setFormData: React.Dispatch<React.SetStateAction<DealFormData>>;
  clientIdProp?: string;

  /** Il client selezionato (dato pieno), già risolto dal parent. */
  selectedClient: Client | null;
  setLoadedClient: (c: Client) => void;
  setSearchResults: React.Dispatch<React.SetStateAction<any[]>>;
  setClientSearchInput: (v: string) => void;
  debouncedSearch: (q: string) => void;

  users: Array<{ id: string; full_name?: string; email?: string }>;
  currentUserId?: string;
  selectStyles: any;
  handleCreateClient: (inputValue: string) => void;

  // Quick client inline
  showQuickClientInput: boolean;
  setShowQuickClientInput: (v: boolean) => void;
  newClientName: string;
  setNewClientName: (v: string) => void;
  handleQuickClientSubmit: (data: QuickClientData) => Promise<void> | void;
  handleExpandToFullForm: (data: QuickClientData) => void;

  // Contatti dell'offerta = persone-referente selezionate (id persona clients)
  selectedContactIds: string[];
  setSelectedContactIds: React.Dispatch<React.SetStateAction<string[]>>;

  /** Scelta dell'indirizzo di spedizione, mostrata con i dati del cliente */
  shippingLocationSlot?: React.ReactNode;
}

/**
 * Card "Cliente" del DealForm step 1, ridisegnata Notion-like.
 *
 * Comportamento:
 *  - Senza cliente selezionato: solo il search compatto + "+ Nuovo"
 *    (header collassato, padding minimo).
 *  - Cliente selezionato: il search si trasforma in "chip" con nome +
 *    clear button, sotto la lista compatta dei dati cliente (email,
 *    telefono, P.IVA, indirizzo) e infine un collapsible per i Contatti
 *    da associare all'offerta.
 *
 * La tendina dei risultati del select viene renderizzata in un portal
 * (`menuPortalTarget={document.body}`) per evitare il clipping dovuto
 * al container `overflow-y-auto` della top fissa.
 */
export function DealClientSelectorCard({
  variant,
  isDark,
  language,
  allClientOptions,
  filteredAndSortedClientOptions,
  isSearching,
  clientsLoading,
  formData,
  setFormData,
  clientIdProp,
  selectedClient,
  setLoadedClient,
  setSearchResults,
  setClientSearchInput,
  debouncedSearch,
  users,
  currentUserId,
  selectStyles,
  handleCreateClient,
  showQuickClientInput,
  setShowQuickClientInput,
  newClientName,
  setNewClientName,
  handleQuickClientSubmit,
  handleExpandToFullForm,
  localContacts,
  selectedContactIds,
  setSelectedContactIds,
  handleContactsChange,
  shippingLocationSlot,
}: DealClientSelectorCardProps) {
  const [contactsOpen, setContactsOpen] = useState(false);
  const isMobile = variant === 'mobile';

  const onSelectChange = (selected: ClientOption | null) => {
    setFormData({ ...formData, client_id: selected?.value || '' });
    if (selected?.data) {
      setLoadedClient(selected.data as Client);
      setSearchResults(prev => {
        const exists = prev.some((r: any) => r.id === selected.value);
        return exists ? prev : [...prev, selected.data];
      });
    }
  };

  const clearClient = () => {
    setFormData({ ...formData, client_id: '' });
  };

  // Stile Notion-like riusabile per react-select.
  const notionSelectStyles = {
    ...selectStyles,
    control: (base: any, state: any) => ({
      ...base,
      minHeight: '32px',
      background: state.isFocused
        ? (isDark ? 'rgba(255, 255, 255, 0.06)' : 'white')
        : (isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)'),
      borderColor: state.isFocused
        ? (isDark ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)')
        : 'transparent',
      boxShadow: 'none',
      borderRadius: '6px',
      paddingLeft: '24px',
      fontSize: '13px',
      transition: 'border-color 0.15s ease, background 0.15s ease',
      '&:hover': {
        borderColor: isDark ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
        background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
      },
    }),
    valueContainer: (base: any) => ({ ...base, padding: '2px 6px' }),
    indicatorsContainer: (base: any) => ({ ...base, height: '32px' }),
    placeholder: (base: any) => ({
      ...base,
      color: isDark ? 'rgba(255, 255, 255, 0.35)' : 'rgba(0, 0, 0, 0.4)',
      fontSize: '13px',
    }),
    menu: (base: any) => ({
      ...base,
      background: isDark ? 'rgba(30, 30, 30, 0.98)' : 'white',
      border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
      borderRadius: '8px',
      boxShadow: isDark
        ? '0 8px 24px rgba(0, 0, 0, 0.5)'
        : '0 8px 24px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)',
      marginTop: '4px',
      overflow: 'hidden',
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 99999 }),
    menuList: (base: any) => ({ ...base, padding: '4px' }),
    option: (base: any, state: any) => ({
      ...base,
      background: state.isFocused
        ? (isDark ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)')
        : 'transparent',
      color: isDark ? 'white' : '#1a1a1a',
      padding: '6px 8px',
      borderRadius: '4px',
      fontSize: '13px',
      cursor: 'pointer',
    }),
    noOptionsMessage: (base: any) => ({
      ...base,
      color: isDark ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
      fontSize: '12px',
      padding: '12px',
    }),
  };

  const selectedValue = allClientOptions.find(o => o.value === (formData.client_id || clientIdProp));

  // ---------- HEADER COMPATTO ----------
  const header = (
    <div className="flex items-center justify-between mb-1.5">
      <div className="flex items-center gap-1.5">
        <Building2 className="h-3 w-3 text-black/55 dark:text-white/55" />
        <span className="text-[11px] font-medium uppercase tracking-wide text-black/55 dark:text-white/55">
          {language === 'it' ? 'Cliente' : 'Client'}
        </span>
      </div>
      {!selectedClient && (
        <button
          type="button"
          onClick={() => {
            setNewClientName('');
            setShowQuickClientInput(true);
          }}
          className="flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
        >
          <UserPlus className="h-3 w-3" />
          {language === 'it' ? 'Nuovo' : 'New'}
        </button>
      )}
    </div>
  );

  // ---------- SEARCH SELECT (stato "no client") ----------
  const searchSelect = (
    <div className="relative">
      <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-black/35 dark:text-white/35 pointer-events-none z-10" />
      <CreatableSelect
        isLoading={variant === 'desktop' ? isSearching : clientsLoading}
        options={variant === 'desktop' ? filteredAndSortedClientOptions : allClientOptions}
        value={selectedValue}
        onChange={onSelectChange as any}
        onInputChange={variant === 'desktop' ? (value, action) => {
          if (action.action === 'input-change') {
            setClientSearchInput(value);
            debouncedSearch(value);
          }
        } : undefined}
        onCreateOption={handleCreateClient}
        placeholder={language === 'it' ? 'Cerca o crea un cliente…' : 'Search or create a client…'}
        formatCreateLabel={(inputValue) => (
          <div className="flex items-center text-[12px]">
            <UserPlus className="h-3 w-3 mr-1.5" />
            {language === 'it' ? `Crea "${inputValue}"` : `Create "${inputValue}"`}
          </div>
        )}
        formatOptionLabel={variant === 'desktop' ? (option: any) => {
          const client = option.data;
          const assignedUser = client?.assigned_to
            ? users.find(u => u.id === client.assigned_to)
            : null;
          const isOtherAgent = assignedUser && client.assigned_to !== currentUserId;
          return (
            <div className="py-0.5">
              <div className="font-medium text-[13px]">{option.label}</div>
              {(client?.vat_number || assignedUser) && (
                <div className={`text-[11px] mt-0.5 flex items-center gap-1.5 ${isDark ? 'text-white/45' : 'text-black/45'}`}>
                  {client?.vat_number && <span>P.IVA {client.vat_number}</span>}
                  {client?.vat_number && assignedUser && <span>·</span>}
                  {assignedUser && (
                    <span className={isOtherAgent ? (isDark ? 'text-amber-400/70' : 'text-amber-600/70') : ''}>
                      {assignedUser.full_name}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        } : undefined}
        isClearable
        isSearchable
        filterOption={variant === 'desktop' ? () => true : undefined}
        noOptionsMessage={(obj) => {
          if (variant === 'desktop' && isSearching) return language === 'it' ? 'Ricerca in corso…' : 'Searching…';
          if (variant === 'desktop' && obj.inputValue && obj.inputValue.length < 2) {
            return language === 'it' ? 'Digita almeno 2 caratteri' : 'Type at least 2 characters';
          }
          return obj.inputValue
            ? (language === 'it' ? 'Nessun cliente trovato' : 'No clients found')
            : (language === 'it' ? 'Inizia a digitare per cercare' : 'Start typing to search');
        }}
        classNamePrefix="react-select"
        styles={notionSelectStyles}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
      />
    </div>
  );

  // ---------- CLIENT CHIP (stato "selected") ----------
  const clientChip = selectedClient && (
    <div className="group flex items-center gap-2 px-2 py-1.5 bg-black/[0.03] dark:bg-white/[0.04] hover:bg-black/[0.05] dark:hover:bg-white/[0.06] rounded-md transition-colors">
      <div className="h-5 w-5 rounded bg-blue-500/15 text-blue-700 dark:bg-blue-400/15 dark:text-blue-300 flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
        {selectedClient.business_name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-black dark:text-white truncate leading-tight">
          {selectedClient.business_name}
        </div>
        {(() => {
          const assignedUser = selectedClient.assigned_to
            ? users.find(u => u.id === selectedClient.assigned_to)
            : null;
          if (!assignedUser) return null;
          return (
            <div className="text-[11px] text-black/45 dark:text-white/45 truncate leading-tight">
              {assignedUser.full_name}
            </div>
          );
        })()}
      </div>
      <button
        type="button"
        onClick={clearClient}
        title={language === 'it' ? 'Cambia cliente' : 'Change client'}
        className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-black/[0.06] dark:hover:bg-white/[0.08] transition-opacity"
      >
        <X className="h-3 w-3 text-black/55 dark:text-white/55" />
      </button>
    </div>
  );

  // ---------- DATI CLIENTE COMPATTI ----------
  const clientMetadata = selectedClient && (
    <div className="space-y-0.5 text-[11.5px] pl-1">
      {selectedClient.email && (
        <MetaRow icon={Mail}>{selectedClient.email}</MetaRow>
      )}
      {selectedClient.phone && (
        <MetaRow icon={Phone}>{selectedClient.phone}</MetaRow>
      )}
      {selectedClient.whatsapp_number && selectedClient.whatsapp_number !== selectedClient.phone && (
        <MetaRow icon={Phone} tone="green">
          {selectedClient.whatsapp_number} <span className="text-black/35 dark:text-white/35">· WhatsApp</span>
        </MetaRow>
      )}
      {(selectedClient.city || selectedClient.street_address) && (
        <MetaRow icon={MapPin}>
          {[selectedClient.street_address, selectedClient.city, selectedClient.province].filter(Boolean).join(', ')}
        </MetaRow>
      )}
      {selectedClient.vat_number && (
        <MetaRow icon={Hash}>P.IVA {selectedClient.vat_number}</MetaRow>
      )}
      {selectedClient.tax_code && selectedClient.tax_code !== selectedClient.vat_number && (
        <MetaRow icon={Hash}>C.F. {selectedClient.tax_code}</MetaRow>
      )}
    </div>
  );

  // ---------- CONTATTI COLLAPSIBLE ----------
  const contactsBlock = selectedClient && (
    <div className="pt-0.5">
      <button
        type="button"
        onClick={() => setContactsOpen(o => !o)}
        className={`w-full flex items-center gap-1.5 px-2 py-1.5 bg-black/[0.025] dark:bg-white/[0.04] hover:bg-black/[0.05] dark:hover:bg-white/[0.06] border border-black/[0.05] dark:border-white/[0.06] transition-colors ${
          contactsOpen ? 'rounded-t-md border-b-0' : 'rounded-md'
        }`}
      >
        {contactsOpen
          ? <ChevronDown className="h-3 w-3 text-black/55 dark:text-white/55" />
          : <ChevronRight className="h-3 w-3 text-black/55 dark:text-white/55" />}
        <Users className="h-3 w-3 text-black/55 dark:text-white/55" />
        <span className="text-[12px] font-medium text-black/75 dark:text-white/75">
          {language === 'it' ? 'Contatti per l\'offerta' : 'Contacts for offer'}
        </span>
        {selectedContactIds.length > 0 && (
          <span className="text-[11px] text-black/45 dark:text-white/45">
            ({selectedContactIds.length})
          </span>
        )}
      </button>
      {contactsOpen && (
        <div className="px-2 py-2 border border-t-0 border-black/[0.05] dark:border-white/[0.06] rounded-b-md bg-white/40 dark:bg-white/[0.02]">
          {selectedClient?.entity_type === 'company' ? (
            <DealReferentiSelector
              companyClientId={selectedClient.id}
              selectedPersonIds={selectedContactIds}
              onSelectionChange={setSelectedContactIds}
            />
          ) : (
            <p className="text-[12px] text-black/40 dark:text-white/40">
              Nessun referente: il cliente è una persona.
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className={`${isMobile ? 'px-3 py-3' : 'px-4 py-3'} space-y-2 bg-white dark:bg-white/[0.04] border-b border-black/[0.08] dark:border-white/[0.08]`}>
      {header}

      {selectedClient ? (
        <>
          {clientChip}
          {clientMetadata}
          <DealWhatsAppButton client={selectedClient} />
          {shippingLocationSlot}
          {contactsBlock}
        </>
      ) : (
        searchSelect
      )}

      {showQuickClientInput && (
        <div className="pt-1">
          <ClientQuickInput
            onSubmit={handleQuickClientSubmit}
            onCancel={() => setShowQuickClientInput(false)}
            onExpandForm={handleExpandToFullForm}
            autoFocus
            initialName={newClientName}
          />
        </div>
      )}
    </div>
  );
}

interface MetaRowProps {
  icon: React.ComponentType<{ className?: string }>;
  tone?: 'default' | 'green';
  children: React.ReactNode;
}

function MetaRow({ icon: Icon, tone = 'default', children }: MetaRowProps) {
  const color = tone === 'green'
    ? 'text-green-600 dark:text-green-400'
    : 'text-black/65 dark:text-white/65';
  return (
    <div className={`flex items-center gap-1.5 ${color}`}>
      <Icon className="h-3 w-3 flex-shrink-0 opacity-70" />
      <span className="truncate">{children}</span>
    </div>
  );
}
